import { useState, type FormEvent } from "react";
import { categoryList } from "@/utils/content";

const FORM_URL = "https://readdy.ai/api/form/dah6nuboh653ivfvnml0";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("contact_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交未成功，请稍后重试");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请稍后再试一次");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-100">
      <div className="mx-auto max-w-content px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <span className="flex w-fit items-center gap-2 rounded-md bg-primary-100 px-2.5 py-1 text-xs font-medium tracking-wide text-primary-700">
              <i className="ri-mail-send-line text-sm"></i>
              订阅瓜报
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              有新的事件复盘，第一时间告诉你
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              瓜报只在你关注的分类出现值得复盘的事件时发送，包含事件概览与时间线要点，
              不会频繁打扰，随时可以退订。
            </p>
            <ul className="mt-6 space-y-2.5">
              {["按事件梳理，附时间线要点", "可选择你更关注的主题分类", "重要节点更新时才发送"].map(
                (item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2.5 text-sm text-foreground-700"
                  >
                    <i className="ri-checkbox-circle-line text-lg text-secondary-600"></i>
                    {item}
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="w-full">
            {status === "success" ? (
              <div className="rounded-lg border border-secondary-300 bg-secondary-100 p-6 text-center">
                <i className="ri-mail-check-line text-3xl text-secondary-700"></i>
                <h3 className="mt-3 font-heading text-lg font-semibold text-secondary-900">
                  订阅成功，瓜报已为你排上号
                </h3>
                <p className="mt-2 text-sm text-secondary-900/80">
                  有新的事件时间线更新时，我们会发送到你填写的邮箱。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-4 cursor-pointer rounded-md border border-secondary-300 px-4 py-2 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  再订阅一个邮箱
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="hot-melon-subscribe-form"
                className="flex flex-col gap-4"
              >
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="subscribe-email"
                    className="text-sm font-medium text-foreground-800"
                  >
                    邮箱地址
                  </label>
                  <input
                    id="subscribe-email"
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="subscribe-interest"
                    className="text-sm font-medium text-foreground-800"
                  >
                    更关注的主题（可选）
                  </label>
                  <select
                    id="subscribe-interest"
                    name="interest"
                    defaultValue=""
                    className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">全部主题</option>
                    {categoryList.map((category) => (
                      <option key={category.slug} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <input
                  type="text"
                  name="contact_alt"
                  className="extra-field-guard"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "error" && (
                  <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700">
                    <i className="ri-error-warning-line text-base"></i>
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i
                    className={
                      status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"
                    }
                  ></i>
                  {status === "submitting" ? "提交中…" : "订阅瓜报"}
                </button>
                <p className="text-xs leading-relaxed text-foreground-500">
                  提交即表示同意我们使用该邮箱发送事件复盘提醒，我们不会将邮箱用于其他用途。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}